// =============================================================================
// Hardware integration layer.
// Browsers cannot talk to native USB receipt printers / cash drawers directly
// without WebUSB/WebSerial or a local print bridge. These functions provide a
// clean abstraction: they attempt WebSerial/WebUSB where available and fall
// back to browser printing (window.print of a formatted receipt) otherwise.
// =============================================================================
import { THOMSONS_LOGO } from '../lib/assets'

export interface ReceiptLine {
  name: string
  qty: number
  price: number
  total: number
}

export interface ReceiptData {
  invoiceNumber: number | string
  date: string
  cashier?: string
  customer?: string
  lines: ReceiptLine[]
  subtotal: number
  tax: number
  total: number
  tendered: number
  change: number
  paymentMethod: string
}

const STORE_NAME = "Thomson's Landscaping & Garden Center"

/** Returns true if the browser exposes WebSerial (for serial receipt printers). */
export function hasSerialSupport(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator
}

/** Returns true if the browser exposes WebUSB. */
export function hasUsbSupport(): boolean {
  return typeof navigator !== 'undefined' && 'usb' in navigator
}

/** Build a plain-text 40-column receipt (matches thermal printer width). */
export function formatReceiptText(r: ReceiptData): string {
  const W = 40
  const line = (l: string, right = '') => {
    const space = Math.max(1, W - l.length - right.length)
    return l + ' '.repeat(space) + right
  }
  const sep = '-'.repeat(W)
  const out: string[] = []
  out.push(center(STORE_NAME, W))
  out.push(center('Sales Receipt', W))
  out.push(sep)
  out.push(line(`Invoice #${r.invoiceNumber}`))
  out.push(line(`Date: ${r.date}`))
  if (r.cashier) out.push(line(`Cashier: ${r.cashier}`))
  if (r.customer) out.push(line(`Customer: ${r.customer}`))
  out.push(sep)
  for (const item of r.lines) {
    out.push(line(item.name.slice(0, W)))
    out.push(line(`  ${item.qty} x ${money(item.price)}`, money(item.total)))
  }
  out.push(sep)
  out.push(line('Subtotal', money(r.subtotal)))
  out.push(line('Tax', money(r.tax)))
  out.push(line('TOTAL', money(r.total)))
  out.push(line(r.paymentMethod, money(r.tendered)))
  out.push(line('Change', money(r.change)))
  out.push(sep)
  out.push(center('Thank you for your business!', W))
  return out.join('\n')
}

function center(s: string, w: number): string {
  if (s.length >= w) return s.slice(0, w)
  const pad = Math.floor((w - s.length) / 2)
  return ' '.repeat(pad) + s
}

function money(n: number): string {
  return '$' + (n ?? 0).toFixed(2)
}

/**
 * Print a receipt. Tries WebSerial ESC/POS first; falls back to a browser
 * print window with a monospace layout that works on any printer.
 */
export async function printReceipt(r: ReceiptData): Promise<{ method: string }> {
  const text = formatReceiptText(r)

  if (hasSerialSupport()) {
    try {
      // @ts-expect-error - WebSerial types are not in lib.dom for all TS versions
      const port = await navigator.serial.requestPort()
      await port.open({ baudRate: 9600 })
      const encoder = new TextEncoder()
      const writer = port.writable.getWriter()
      // ESC @ = initialize, text, feed + partial cut
      await writer.write(encoder.encode('\x1B\x40' + text + '\n\n\n\x1D\x56\x42\x00'))
      writer.releaseLock()
      await port.close()
      return { method: 'serial' }
    } catch {
      // user cancelled or no device — fall through to browser print
    }
  }

  // Browser print fallback
  const win = window.open('', '_blank', 'width=380,height=600')
  if (win) {
    win.document.write(
      `<div style="text-align:center;margin:8px 0"><img src="${THOMSONS_LOGO}" style="width:120px;height:auto" alt="Thomson's"/></div>` +
      `<pre style="font-family:monospace;font-size:12px;white-space:pre-wrap;">${escapeHtml(text)}</pre>`
    )
    win.document.close()
    win.focus()
    win.print()
    return { method: 'browser-print' }
  }
  return { method: 'none' }
}

export interface ZoutLine { label: string; count: number; amount: number; refund: number }
export interface ZReportData {
  day: string
  register: string                 // "ALL" or a register number
  cashier?: string
  payments: ZoutLine[]
  payouts: { count: number; amount: number; refund: number }
  register_total: { count: number; amount: number; refund: number }
  taxable_sales: number
  nontax_sales: number
  net_sales: number
  tax_total: number
  total: number
  voids: number
  management_voids: number
  refund_count: number
  refund_amount: number
  check_change_count: number
  check_change_amount: number
  suspended: number
  inprogress_count: number
  inprogress_amount: number
  departments: { name: string; amount: number }[]
}

/**
 * Build the Z-Out reconciliation text — faithful port of the legacy POS
 * ZOutDiag receipt (Thomson's Landscaping). 40-column thermal layout: tender
 * breakdown with [refund] brackets, payouts, register total, taxable/non-tax/
 * net sales, tax, total, voids/refunds/suspended/in-progress, dept totals.
 */
export function formatZReportText(z: ZReportData): string {
  const W = 40
  const out: string[] = []
  // Header (centered, like the legacy fixed-indent header)
  out.push(center("Thomson's Landscaping LLC", W))
  out.push(center('Z-Out Report', W))
  out.push(center(`Store: 1 --- Register: ${z.register}`, W))
  out.push(center(z.day, W))
  if (z.cashier) out.push(center(z.cashier, W))
  out.push('')

  // Tender lines: "Label            cnt   $amount [$refund]"
  const tender = (l: ZoutLine) => {
    const left = `${l.label.padEnd(16)} ${String(l.count).padStart(4)}`
    const right = `${money(l.amount)} [${money(l.refund)}]`
    const space = Math.max(1, W - left.length - right.length)
    return left + ' '.repeat(space) + right
  }
  for (const p of z.payments) out.push(tender(p))
  out.push('')
  out.push(tender({ label: 'Pay Outs', ...z.payouts }))
  out.push('')
  out.push(tender({ label: 'Register/s Total', ...z.register_total }))
  out.push('')

  // Sales section
  const kv = (l: string, v: string) => {
    const space = Math.max(1, W - l.length - v.length)
    return l + ' '.repeat(space) + v
  }
  out.push(kv('Taxable Sales', money(z.taxable_sales)))
  out.push(kv('Non-Tax Sales', money(z.nontax_sales)))
  out.push(' '.repeat(Math.max(0, W - 8)) + '--------')
  out.push(kv('Net Sales', money(z.net_sales)))
  out.push('')
  out.push(kv('Tax Total', money(z.tax_total)))
  out.push('')
  out.push(kv('Total', money(z.total)))
  out.push('')

  // Counts / exceptions
  out.push(kv('Voids', String(z.voids)))
  out.push(kv('Management Voids', money(z.management_voids)))
  out.push(kv(`Total Refunds  ${z.refund_count}`, money(z.refund_amount)))
  out.push(kv(`Check Change  ${z.check_change_count}`, money(z.check_change_amount)))
  out.push(kv('Suspended', String(z.suspended)))
  out.push(kv(`In-Progress W/ Payment  ${z.inprogress_count}`, money(z.inprogress_amount)))
  out.push('')

  // Department totals
  out.push('Department Totals')
  out.push('-'.repeat(17))
  let deptTotal = 0
  for (const d of z.departments) {
    deptTotal += d.amount
    out.push(kv(d.name.slice(0, 24), money(d.amount)))
  }
  out.push(' '.repeat(Math.max(0, W - 11)) + '-'.repeat(11))
  out.push(kv('', money(deptTotal)))
  return out.join('\n')
}

/** Print a Z-Out reconciliation to the receipt printer (serial ESC/POS or browser). */
export async function printZReport(z: ZReportData): Promise<{ method: string }> {
  const text = formatZReportText(z)

  if (hasSerialSupport()) {
    try {
      // @ts-expect-error WebSerial
      const port = await navigator.serial.requestPort()
      await port.open({ baudRate: 9600 })
      const encoder = new TextEncoder()
      const writer = port.writable.getWriter()
      await writer.write(encoder.encode('\x1B\x40' + text + '\n\n\n\x1D\x56\x42\x00'))
      writer.releaseLock()
      await port.close()
      return { method: 'serial' }
    } catch {
      // fall through to browser print
    }
  }

  const win = window.open('', '_blank', 'width=380,height=600')
  if (win) {
    win.document.write(
      `<div style="text-align:center;margin:8px 0"><img src="${THOMSONS_LOGO}" style="width:120px;height:auto" alt="Thomson's"/></div>` +
      `<pre style="font-family:monospace;font-size:12px;white-space:pre-wrap;">${escapeHtml(text)}</pre>`
    )
    win.document.close()
    win.focus()
    win.print()
    return { method: 'browser-print' }
  }
  return { method: 'none' }
}

/** Open the cash drawer (kick pulse via serial ESC/POS, if connected). */
export async function openCashDrawer(): Promise<boolean> {
  if (!hasSerialSupport()) return false
  try {
    // @ts-expect-error WebSerial
    const port = await navigator.serial.requestPort()
    await port.open({ baudRate: 9600 })
    const writer = port.writable.getWriter()
    // ESC p 0 25 250 — standard drawer kick pulse
    await writer.write(new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]))
    writer.releaseLock()
    await port.close()
    return true
  } catch {
    return false
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
